"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  MessageSquare,
  FileText,
  Tag,
  ShieldAlert,
  ChevronUp,
  History,
} from "lucide-react";
import { useMediaQuery, Drawer } from "@mui/material";
import "./contract-analysis.css";
import { contractUploadApi, contractRunAnalysisApi } from "../../services/api/contractAnalysisApi";
import { RAG_CONTRACT_ENDPOINTS } from "../../services/apiEndpoints";
import { BACKEND_ENDPOINTS } from "../../services/apiEndpoints";
import {
  CONTRACT_ANALYSIS_SESSIONS_KEY,
  CONTRACT_ANALYSIS_ACTIVE_SESSION_KEY,
} from "../../utils/HelperFunction/Constants";
import ContractAnalysisSidebar from "../../components/layout/ContractAnalysisSidebar";

// ── Static data ───────────────────────────────────────────────────────────────
const STEPS = [
  "Parsing document structure",
  "Extracting clauses & provisions",
  "Running risk assessment",
  "Generating summary & insights",
];

const ANALYSIS_BUTTONS = [
  {
    key: "summarization",
    label: "Summarization",
    desc: "Concise overview of the full contract",
    icon: MessageSquare,
    endpoint: RAG_CONTRACT_ENDPOINTS.ANALYSIS_SUMMARIZATION,
    accent: "#534AB7",
    accentBg: "#EEEDFE",
    accentBorder: "#AFA9EC",
  },
  {
    key: "legality",
    label: "Legality",
    desc: "Legal compliance & validity check",
    endpoint: RAG_CONTRACT_ENDPOINTS.ANALYSIS_LEGALITY,
    icon: FileText,
    accent: "#0F6E56",
    accentBg: "#E1F5EE",
    accentBorder: "#5DCAA5",
  },
  {
    key: "bias",
    label: "Bias Detection",
    desc: "Identify one-sided or unfair clauses",
    endpoint: RAG_CONTRACT_ENDPOINTS.ANALYSIS_BIAS,
    icon: Tag,
    accent: "#185FA5",
    accentBg: "#E6F1FB",
    accentBorder: "#85B7EB",
  },
  {
    key: "template",
    label: "Template Check",
    desc: "Compare against standard templates",
    endpoint: RAG_CONTRACT_ENDPOINTS.ANALYSIS_TEMPLATE,
    icon: ShieldAlert,
    accent: "#993C1D",
    accentBg: "#FAECE7",
    accentBorder: "#F0997B",
  },
];

// ── Session management (localStorage) ────────────────────────────────────────
function useContractSessions() {
  const [sessions, setSessions] = useState(() => {
    try {
      const stored = localStorage.getItem(CONTRACT_ANALYSIS_SESSIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [activeSessionId, setActiveSessionId] = useState(() => {
    return localStorage.getItem(CONTRACT_ANALYSIS_ACTIVE_SESSION_KEY) || null;
  });

  useEffect(() => {
    try {
      localStorage.setItem(CONTRACT_ANALYSIS_SESSIONS_KEY, JSON.stringify(sessions));
    } catch {}
  }, [sessions]);

  useEffect(() => {
    if (activeSessionId) {
      localStorage.setItem(CONTRACT_ANALYSIS_ACTIVE_SESSION_KEY, activeSessionId);
    } else {
      localStorage.removeItem(CONTRACT_ANALYSIS_ACTIVE_SESSION_KEY);
    }
  }, [activeSessionId]);

  const createSession = useCallback((session) => {
    setSessions((prev) => [session, ...prev]);
  }, []);

  const updateSession = useCallback((id, updates) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  }, []);

  const deleteSession = useCallback((id) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const pinSession = useCallback((id, isPinned) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isPinned } : s))
    );
  }, []);

  const renameSession = useCallback((id, title) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, title } : s))
    );
  }, []);

  const getSession = useCallback(
    (id) => sessions.find((s) => s.id === id),
    [sessions]
  );

  return {
    sessions,
    activeSessionId,
    setActiveSessionId,
    createSession,
    updateSession,
    deleteSession,
    pinSession,
    renameSession,
    getSession,
  };
}

// ── Custom Hook for Contract Analysis ─────────────────────────────────────────
function useContractAnalysis(initialSession) {
  const [contractText, setContractText] = useState(initialSession?.contractText || "");
  const [fileName, setFileName] = useState(initialSession?.fileName || "");
  const [fileSize, setFileSize] = useState(initialSession?.fileSize || "");
  const [charCount, setCharCount] = useState(initialSession?.charCount || 0);
  const [phase, setPhase] = useState(initialSession?.contractText ? "done" : "idle");
  const [uploadError, setUploadError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [results, setResults] = useState(initialSession?.results || {});
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const uploadContract = useCallback(async (file) => {
    const fmt = (bytes) =>
      bytes < 1024 * 1024
        ? (bytes / 1024).toFixed(0) + " KB"
        : (bytes / 1024 / 1024).toFixed(1) + " MB";

    setPhase("analyzing");
    setUploadError(null);
    setFileName(file.name);
    setFileSize(fmt(file.size));
    setContractText("");
    setCharCount(0);

    try {
      const uploadResponse = await contractUploadApi(file);
      const { data } = uploadResponse;
      setContractText(data.text);
      setFileName(data.filename || file.name);
      setCharCount(data.char_count ?? 0);
      setPhase("done");

      return { success: true, data };
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Upload failed. Please try again.";

      setPhase("error");
      setUploadError(message);
      return { success: false, error: message };
    }
  }, []);

  const runAnalysis = useCallback(
    async (btn) => {
      if (analysisLoading) return;

      if (results[btn.key]) {
        setActiveKey(btn.key);
        setAnalysisError(null);
        return;
      }

      setActiveKey(btn.key);
      setAnalysisError(null);
      setAnalysisLoading(true);

      try {
        const analysisResponse = await contractRunAnalysisApi(btn.endpoint, contractText);
        const { data } = analysisResponse;

        setResults((prev) => ({
          ...prev,
          [btn.key]: data.result ?? "",
        }));
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Analysis failed. Please try again.";
        setAnalysisError(msg);
      } finally {
        setAnalysisLoading(false);
      }
    },
    [analysisLoading, results, contractText],
  );

  const resetContract = useCallback(() => {
    setContractText("");
    setFileName("");
    setFileSize("");
    setCharCount(0);
    setPhase("idle");
    setUploadError(null);
    setActiveKey(null);
    setResults({});
    setAnalysisError(null);
    setAnalysisLoading(false);
  }, []);

  const loadSession = useCallback((session) => {
    setContractText(session.contractText || "");
    setFileName(session.fileName || "");
    setFileSize(session.fileSize || "");
    setCharCount(session.charCount || 0);
    setResults(session.results || {});
    setActiveKey(null);
    setPhase(session.contractText ? "done" : "idle");
    setUploadError(null);
    setAnalysisError(null);
    setAnalysisLoading(false);
  }, []);

  return {
    contractText,
    fileName,
    fileSize,
    charCount,
    phase,
    uploadError,
    activeKey,
    results,
    analysisError,
    analysisLoading,
    uploadContract,
    runAnalysis,
    resetContract,
    loadSession,
    setActiveKey,
  };
}

function useContractChat(contractText, contractLoaded, initialMessages = [], initialApiMessages = []) {
  const [messages, setMessages] = useState(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const apiMessagesRef = useRef(initialApiMessages);

  const sendChatMessage = useCallback(
    async ({ trimmed, apiMessages, onChunk, onDone, onError }) => {
      const messages = [...apiMessages];

      if (messages.length === 0) {
        messages.push(
          {
            role: "system",
            content: "You are a legal analyst. Contract:\n\n" + contractText,
          },
          { role: "user", content: trimmed },
        );
      } else {
        messages.push({ role: "user", content: trimmed });
      }

      let accumulated = "";

      try {
        const response = await fetch(`${BACKEND_ENDPOINTS.AI_API_BASE_URL}${RAG_CONTRACT_ENDPOINTS.CONTRACT_CHAT}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(
            errData.detail ||
              errData.message ||
              `Server error ${response.status}`,
          );
        }

        const data = await response.json();
        accumulated =
          data.answer ??
          data.result ??
          data.content ??
          data.message ??
          JSON.stringify(data);

        onChunk(accumulated);
        const finalText = accumulated || "No response received.";
        onDone(finalText, messages);
      } catch (err) {
        onError(err.message || "Failed to get a response. Please try again.");
      }
    },
    [contractText],
  );

  const sendMessage = useCallback(
    async (text, nowFn) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const msgTime = nowFn();

      setMessages((prev) => [
        ...prev,
        { role: "user", html: trimmed, time: msgTime },
      ]);

      setIsLoading(true);

      const loadingMsgTime = nowFn();
      setMessages((prev) => [
        ...prev,
        { role: "ai", html: "", time: loadingMsgTime, loading: true },
      ]);

      const updateBubble = (html, isLoading = false) => {
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastIdx = newMessages.length - 1;
          if (newMessages[lastIdx]?.role === "ai") {
            newMessages[lastIdx] = {
              ...newMessages[lastIdx],
              html,
              loading: isLoading,
            };
          }
          return newMessages;
        });
      };

      await sendChatMessage({
        trimmed,
        apiMessages: apiMessagesRef.current,

        onChunk: (text) => updateBubble(text, true),

        onDone: (finalText, updatedMessages) => {
          apiMessagesRef.current = [
            ...updatedMessages,
            { role: "assistant", content: finalText },
          ];
          updateBubble(finalText, false);
          setIsLoading(false);
        },

        onError: (errMsg) => {
          apiMessagesRef.current.pop();
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastIdx = newMessages.length - 1;
            if (
              newMessages[lastIdx]?.role === "ai" &&
              newMessages[lastIdx]?.loading
            ) {
              newMessages[lastIdx] = {
                role: "ai",
                html: `⚠ ${errMsg}`,
                time: loadingMsgTime,
                loading: false,
              };
            } else {
              newMessages.push({
                role: "ai",
                html: `⚠ ${errMsg}`,
                time: nowFn(),
                loading: false,
              });
            }
            return newMessages;
          });
          setIsLoading(false);
        },
      });
    },
    [isLoading, sendChatMessage],
  );

  const loadSession = useCallback((savedMessages, savedApiMessages) => {
    setMessages(savedMessages || []);
    apiMessagesRef.current = savedApiMessages || [];
  }, []);

  // Reset chat when contract is unloaded
  useEffect(() => {
    if (!contractLoaded) {
      setMessages([]);
      apiMessagesRef.current = [];
    }
  }, [contractLoaded]);

  // Show greeting when contract is loaded and chat is empty
  useEffect(() => {
    if (contractLoaded && messages.length === 0) {
      const now = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      setMessages([
        {
          role: "ai",
          html: "Your contract has been uploaded successfully. Ask me anything about its terms, clauses, risks, or obligations.",
          time: now,
        },
      ]);
    }
  }, [contractLoaded, messages.length]);

  return {
    messages,
    setMessages,
    isLoading,
    sendMessage,
    loadSession,
    apiMessagesRef,
  };
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return <span className="spinner" />;
}

// ── Upload Zone ───────────────────────────────────────────────────────────────
function UploadZone({ onFile }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
    [onFile],
  );

  return (
    <div
      className={`upload-zone ${dragging ? "dragging" : ""}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <div className="upload-zone-background" />

      <div className={`upload-icon-box ${dragging ? "dragging" : ""}`}>
        <svg
          width="30"
          height="30"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6366f1"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>

      <h2 className="upload-title">Drop your contract here</h2>
      <p className="upload-subtitle">
        Or click to browse and select a file
        <br />
        from your computer
      </p>

      <div className="upload-formats">
        {["PDF", "DOCX", "DOC"].map((fmt) => (
          <span key={fmt} className="format-badge">
            {fmt}
          </span>
        ))}
        <span className="format-separator">· Max 50MB</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
      />
    </div>
  );
}

// ── Progress Card ─────────────────────────────────────────────────────────────
function ProgressCard({ step, pct }) {
  return (
    <div className="progress-card">
      <div className="progress-header">
        <div className="progress-step-info">
          <Spinner />
          <span>{STEPS[step] ?? "Finalising…"}</span>
        </div>
        <span className="progress-percentage">{pct}%</span>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="progress-steps">
        {STEPS.map((s, i) => {
          const done = i < step;
          const active = i === step;
          const stepClass = done ? "done" : active ? "active" : "inactive";
          return (
            <span key={s} className={`progress-step ${stepClass}`}>
              {done ? "✓" : active ? "▶" : "○"} {s}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Analysis Results ──────────────────────────────────────────────────────────
function AnalysisResults({
  fileName,
  fileSize,
  charCount,
  onReset,
  activeKey,
  results,
  analysisError,
  analysisLoading,
  runAnalysis,
  setActiveKey,
}) {
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [openKeys, setOpenKeys] = useState(new Set());

  function toggleKey(key) {
    setOpenKeys((prev) => {
      const next = new Set();
      if (!prev.has(key)) {
        next.add(key);
      }
      return next;
    });
  }

  const handleBtnClick = useCallback(
    (btn) => {
      toggleKey(btn.key);

      if (analysisLoading) return;
      if (results[btn.key]) return;

      runAnalysis(btn);
    },
    [analysisLoading, runAnalysis, results],
  );

  return (
    <div className="analysis-results-container">
      {/* ── Doc card ── */}
      <div className="doc-card">
        <div className="doc-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6366f1"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </div>

        <div className="doc-info">
          <p className="doc-name">{fileName}</p>
          <p className="doc-meta">
            {fileSize}
            {charCount ? ` · ${charCount.toLocaleString()} chars` : ""}
          </p>
        </div>

        <button onClick={onReset} className="remove-btn">
          <svg
            className="remove-btn-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
          Remove
        </button>
      </div>

      {/* ── Analysis buttons ── */}
      <div className="analysis-section">
        <p className="analysis-section-title">Run Analysis</p>

        <div className="analysis-buttons">
          {ANALYSIS_BUTTONS.map((btn) => {
            const isHov = hoveredBtn === btn.key;
            const isCached = !!results[btn.key];
            const isCurrentlyLoading = analysisLoading && activeKey === btn.key;
            const isDisabled = analysisLoading && !isCached;

            return (
              <button
                key={btn.key}
                disabled={isDisabled}
                onClick={() => handleBtnClick(btn)}
                onMouseEnter={() => setHoveredBtn(btn.key)}
                onMouseLeave={() => setHoveredBtn(null)}
                className={`analysis-btn ${isCached ? "cached" : ""} ${isDisabled ? "disabled" : ""}`}
                style={{
                  border: `0.5px solid ${isCached || isHov ? btn.accentBorder : "#e2e8f0"}`,
                  background: isCached
                    ? `${btn.accentBg}60`
                    : isHov
                      ? "#f8fafc"
                      : "#fff",
                  boxShadow: isCached
                    ? `0 0 0 3px ${btn.accentBorder}30`
                    : "none",
                }}
              >
                {isCached && (
                  <span
                    className="cached-dot"
                    style={{ background: btn.accent }}
                  />
                )}

                <div className="btn-icon">
                  <btn.icon
                    size={18}
                    strokeWidth={1.75}
                    color={isCached ? btn.accent : "#94a3b8"}
                    style={{ transition: "color 180ms ease" }}
                  />
                </div>

                <p
                  className="btn-label"
                  style={{ color: isCached ? btn.accent : "#0f172a" }}
                >
                  {btn.label}
                  {isCurrentlyLoading && <Spinner />}
                </p>

                <p className="btn-desc">
                  {isCached ? "Tap to toggle" : btn.desc}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Result panels ── */}
      {ANALYSIS_BUTTONS.filter(
        (btn) => results[btn.key] || (analysisLoading && activeKey === btn.key),
      ).map((btn) => {
        const isOpen = openKeys.has(btn.key);
        const isLoading = analysisLoading && activeKey === btn.key;
        const panelResult = results[btn.key] ?? "";
        const hasError = !isLoading && analysisError && activeKey === btn.key;

        return (
          <div
            key={btn.key}
            className={`result-panel ${isOpen ? "open" : ""}`}
            style={{
              border: `0.5px solid ${isOpen ? btn.accentBorder : "#e2e8f0"}`,
            }}
          >
            <div
              onClick={() => toggleKey(btn.key)}
              className={`panel-header-clickable ${isOpen ? "open" : ""}`}
              style={{
                background: isOpen ? `${btn.accentBg}50` : "#fff",
              }}
            >
              <btn.icon size={14} strokeWidth={1.75} color={btn.accent} />

              <span className="panel-title" style={{ color: btn.accent }}>
                {btn.label}
              </span>

              {isLoading && <Spinner />}

              {hasError && <span className="panel-error-badge">error</span>}

              <span className="panel-status">
                {isLoading ? "Running…" : isOpen ? "Collapse" : "Expand"}
              </span>

              <ChevronUp
                size={13}
                strokeWidth={1.75}
                className={`chevron-icon ${isOpen ? "open" : "closed"}`}
              />
            </div>

            <div className={`panel-body ${isOpen ? "open" : "closed"}`}>
              <div className="panel-content">
                {isLoading && (
                  <div className="loading-state">
                    <Spinner /> Running analysis…
                  </div>
                )}
                {hasError && <p className="error-state">⚠ {analysisError}</p>}
                {!isLoading && panelResult && (
                  <div className="prose-result">
                    <ReactMarkdown>{panelResult}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PanelContent({
  messages,
  isLoading,
  contractLoaded,
  mobileOpen,
  setMobileOpen,
  input,
  setInput,
  textareaRef,
  focused,
  setFocused,
  hoveredSend,
  setHoveredSend,
  onSendMessage,
  onAutoResize,
  onHandleKey,
}) {
  const showEmpty = messages.length === 0;
  const disabled = !contractLoaded;
  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  return (
    <>
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-title">
            <div className="chat-icon">✦</div>
            Ask about Contract
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {contractLoaded && (
              <div className="document-loaded-badge">
                <span className="loaded-dot" />
                Document loaded
              </div>
            )}
            <button
              className="mobile-chat-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close chat"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showEmpty && (
        <div className="chat-empty-state">
          <div className="empty-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <p className="empty-title">No conversation yet</p>
          <p className="empty-description">
            Upload a contract and ask any question about its terms, risks, or
            obligations.
          </p>
        </div>
      )}

      {!showEmpty && (
        <div className="chat-messages" ref={messagesRef}>
          {messages.map((msg, i) => (
            <div key={i} className="message">
              <div className={`message-wrapper ${msg.role}`}>
                <div className={`message-avatar ${msg.role}`}>
                  {msg.role === "ai" ? "L" : "U"}
                </div>
                <div className={`message-bubble ${msg.role}`}>
                  {msg.role === "ai" ? (
                    msg.loading && !msg.html ? (
                      <div className="typing-indicator">
                        {[0, 150, 300].map((delay, idx) => (
                          <span
                            key={idx}
                            className="typing-dot"
                            style={{ animationDelay: `${delay}ms` }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="prose-result prose-chat">
                        <ReactMarkdown>{msg.html}</ReactMarkdown>
                      </div>
                    )
                  ) : (
                    msg.html
                  )}
                </div>
              </div>
              {!msg.loading && (
                <p className={`message-timestamp ${msg.role}`}>{msg.time}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            disabled={disabled}
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              onAutoResize(e.target);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onHandleKey}
            placeholder="Ask anything about this contract…"
            rows={1}
            className={`chat-textarea ${disabled ? "disabled" : ""}`}
            style={{ borderColor: focused ? "#6366f1" : undefined }}
          />
          <button
            disabled={disabled}
            onClick={onSendMessage}
            onMouseEnter={() => setHoveredSend(true)}
            onMouseLeave={() => setHoveredSend(false)}
            className={`send-button ${disabled || isLoading ? "disabled" : ""}`}
            style={{
              background:
                disabled || isLoading
                  ? "#6466d3"
                  : hoveredSend
                    ? "#4f46e5"
                    : "#6366f1",
            }}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

// ChatPanel no longer owns useContractChat — receives messages/isLoading/sendMessage as props
function ChatPanel({
  mobileOpen,
  setMobileOpen,
  contractLoaded,
  messages,
  isLoading,
  sendMessage,
}) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const [hoveredSend, setHoveredSend] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  function now() {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    await sendMessage(trimmed, now);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function autoResize(el) {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }

  const commonProps = {
    messages,
    isLoading,
    contractLoaded,
    mobileOpen,
    setMobileOpen,
    input,
    setInput,
    textareaRef,
    focused,
    setFocused,
    hoveredSend,
    setHoveredSend,
    onSendMessage: handleSend,
    onAutoResize: autoResize,
    onHandleKey: handleKey,
  };

  return (
    <>
      <div className="chat-panel desktop-chat-panel">
        <PanelContent {...commonProps} />
      </div>

      <div className={`mobile-chat-modal ${mobileOpen ? "open" : ""}`}>
        <PanelContent {...commonProps} />
      </div>
    </>
  );
}

export default function ContractAnalysis() {
  // ── Session management ────────────────────────────────────────────────────
  const {
    sessions,
    activeSessionId,
    setActiveSessionId,
    createSession,
    updateSession,
    deleteSession,
    pinSession,
    renameSession,
    getSession,
  } = useContractSessions();

  const initialSession = activeSessionId ? getSession(activeSessionId) : null;

  // ── Contract analysis state ───────────────────────────────────────────────
  const {
    contractText,
    fileName,
    fileSize,
    charCount,
    phase,
    uploadError,
    activeKey,
    results,
    analysisError,
    analysisLoading,
    uploadContract,
    runAnalysis,
    resetContract,
    loadSession: loadContractSession,
    setActiveKey,
  } = useContractAnalysis(initialSession);

  // ── Chat state (lifted to page level for session sync) ────────────────────
  const {
    messages,
    isLoading: chatLoading,
    sendMessage,
    loadSession: loadChatSession,
    apiMessagesRef,
  } = useContractChat(
    contractText,
    phase === "done",
    initialSession?.messages || [],
    initialSession?.apiMessages || [],
  );

  const [progressStep, setProgressStep] = useState(0);
  const [progressPct, setProgressPct] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isMobileView = useMediaQuery("(max-width: 1199px)");

  // ── Sync analysis results to active session ───────────────────────────────
  useEffect(() => {
    if (activeSessionId && phase === "done" && Object.keys(results).length > 0) {
      updateSession(activeSessionId, { results });
    }
  }, [results, activeSessionId, phase, updateSession]);

  // ── Sync chat messages to active session ──────────────────────────────────
  useEffect(() => {
    if (activeSessionId && messages.length > 0) {
      updateSession(activeSessionId, {
        messages,
        apiMessages: apiMessagesRef.current,
      });
    }
  }, [messages, activeSessionId, updateSession]);

  // ── Session handlers ──────────────────────────────────────────────────────
  const handleSessionSelect = useCallback(
    (sessionId) => {
      const session = getSession(sessionId);
      if (!session) return;
      setActiveSessionId(sessionId);
      loadContractSession(session);
      loadChatSession(session.messages || [], session.apiMessages || []);
    },
    [getSession, setActiveSessionId, loadContractSession, loadChatSession],
  );

  const handleNewAnalysis = useCallback(() => {
    setActiveSessionId(null);
    resetContract();
    loadChatSession([], []);
  }, [setActiveSessionId, resetContract, loadChatSession]);

  const handleDeleteSession = useCallback(
    (sessionId) => {
      deleteSession(sessionId);
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        resetContract();
        loadChatSession([], []);
      }
    },
    [deleteSession, activeSessionId, setActiveSessionId, resetContract, loadChatSession],
  );

  // Remove button on doc card — deselects active session but keeps it in history
  const handleRemove = useCallback(() => {
    setActiveSessionId(null);
    resetContract();
    loadChatSession([], []);
  }, [setActiveSessionId, resetContract, loadChatSession]);

  async function handleFile(file) {
    setProgressStep(0);
    setProgressPct(0);
    const uploadPromise = uploadContract(file);
    const targets = [
      { step: 0, pct: 20 },
      { step: 1, pct: 50 },
      { step: 2, pct: 78 },
      { step: 3, pct: 95 },
    ];
    let i = 0;
    const tick = () => {
      if (i < targets.length) {
        const { step, pct } = targets[i++];
        setProgressStep(step);
        setProgressPct(pct);
        setTimeout(tick, 950 + Math.random() * 350);
      }
    };
    tick();
    const result = await uploadPromise;
    if (result.success) {
      setProgressPct(100);
      const fmt = (bytes) =>
        bytes < 1024 * 1024
          ? (bytes / 1024).toFixed(0) + " KB"
          : (bytes / 1024 / 1024).toFixed(1) + " MB";
      const sessionId = `ca_${Date.now()}`;
      createSession({
        id: sessionId,
        title: result.data.filename || file.name,
        fileName: result.data.filename || file.name,
        fileSize: fmt(file.size),
        charCount: result.data.char_count ?? 0,
        contractText: result.data.text,
        results: {},
        messages: [],
        apiMessages: [],
        isPinned: false,
        createdAt: Date.now(),
      });
      setActiveSessionId(sessionId);
      loadChatSession([], []);
    }
  }

  return (
    <div className="contract-analysis-container">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); animation-timing-function: cubic-bezier(0.8,0,1,1); }
          50% { transform: translateY(-25%); animation-timing-function: cubic-bezier(0,0,0.2,1); }
        }
        @keyframes pulse { 50% { opacity: .5; } }
        @keyframes fadein { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeBackdrop { from { opacity: 0; } to { opacity: 1; } }

        .prose-result { font-size: 13.5px; color: #1e293b; line-height: 1.75; }
        .prose-result h1,.prose-result h2,.prose-result h3,.prose-result h4 { font-weight: 700; color: #0f172a; margin: 1em 0 0.4em; line-height: 1.3; }
        .prose-result h1 { font-size: 1.25em; } .prose-result h2 { font-size: 1.1em; } .prose-result h3 { font-size: 1em; }
        .prose-result p { margin: 0.5em 0; }
        .prose-result ul,.prose-result ol { padding-left: 1.4em; margin: 0.5em 0; }
        .prose-result li { margin: 0.25em 0; }
        .prose-result strong { font-weight: 700; color: #0f172a; }
        .prose-result em { font-style: italic; color: #475569; }
        .prose-result code { font-family: ui-monospace,monospace; font-size: 0.85em; background: #f1f5f9; padding: 1px 5px; border-radius: 4px; }
        .prose-result blockquote { border-left: 3px solid #6366f1; margin: 0.75em 0; padding: 4px 12px; color: #64748b; background: #f8fafc; border-radius: 0 6px 6px 0; }
        .prose-result table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin: 0.75em 0; }
        .prose-result th { background: #f1f5f9; font-weight: 700; padding: 8px 12px; text-align: left; border: 1px solid #e2e8f0; }
        .prose-result td { padding: 7px 12px; border: 1px solid #e2e8f0; }
        .prose-result hr { border: none; border-top: 1px solid #e2e8f0; margin: 1em 0; }
        .prose-chat { font-size: 13px; }
        .prose-chat h1,.prose-chat h2,.prose-chat h3,.prose-chat h4 { font-size: 1em; margin: 0.4em 0 0.2em; }
        .prose-chat p:first-child { margin-top: 0; }
        .prose-chat p:last-child { margin-bottom: 0; }
        .prose-chat ul,.prose-chat ol { margin: 0.3em 0; }
        .prose-chat code { font-size: 0.82em; }
      `}</style>

      <div className="ca-content-wrapper">
        {/* Sidebar — Drawer on mobile, inline on desktop */}
        {isMobileView ? (
          <Drawer
            anchor="left"
            open={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            ModalProps={{ keepMounted: true }}
          >
            <ContractAnalysisSidebar
              sessions={sessions}
              activeSessionId={activeSessionId}
              onSessionSelect={(id) => { handleSessionSelect(id); setIsSidebarOpen(false); }}
              onNewAnalysis={() => { handleNewAnalysis(); setIsSidebarOpen(false); }}
              onDeleteSession={handleDeleteSession}
              onPinSession={pinSession}
              onRenameSession={renameSession}
              isMobile
              onClose={() => setIsSidebarOpen(false)}
            />
          </Drawer>
        ) : (
          <ContractAnalysisSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSessionSelect={handleSessionSelect}
            onNewAnalysis={handleNewAnalysis}
            onDeleteSession={handleDeleteSession}
            onPinSession={pinSession}
            onRenameSession={renameSession}
          />
        )}

        {/* Two-column workspace */}
        <div className="workspace-grid">
          <div className="left-panel">
            <div className="panel-header">
              <div>
                <h1 className="title-analysis">
                  Contract Analysis
                  {phase === "done" && (
                    <div className="analysis-complete-badge">
                      <span className="status-dot" />
                      <span className="badge-text">Analysis Complete</span>
                    </div>
                  )}
                </h1>
                <p>Upload a document to extract insights, risks, and key terms</p>
              </div>
              <div className="header-actions">
                {isMobileView && (
                  <button
                    className="history-sidebar-btn"
                    onClick={() => setIsSidebarOpen(true)}
                  >
                    <History size={15} />
                    <span className="history-sidebar-btn-text">History</span>
                  </button>
                )}
                {phase === "done" && (
                  <button
                    className="chat-header-btn active"
                    onClick={() => setMobileOpen(true)}
                  >
                    <div className="chat-header-btn-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <span className="chat-header-btn-text">Chat</span>
                  </button>
                )}
              </div>
            </div>

            <div className="scrollable-content">
              {phase === "idle" && <UploadZone onFile={handleFile} />}
              {phase === "analyzing" && (
                <ProgressCard step={progressStep} pct={progressPct} />
              )}
              {phase === "done" && (
                <AnalysisResults
                  setMobileOpen={setMobileOpen}
                  fileName={fileName}
                  fileSize={fileSize}
                  charCount={charCount}
                  contractText={contractText}
                  onReset={handleRemove}
                  activeKey={activeKey}
                  results={results}
                  analysisError={analysisError}
                  analysisLoading={analysisLoading}
                  runAnalysis={runAnalysis}
                  setActiveKey={setActiveKey}
                />
              )}
              {phase === "error" && (
                <div className="error-container">
                  <p className="error-title">⚠ Upload Failed</p>
                  <p className="error-message">
                    {uploadError || "An unexpected error occurred."}
                  </p>
                  <button onClick={resetContract} className="retry-button">
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Chat */}
          <ChatPanel
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            contractLoaded={phase === "done"}
            messages={messages}
            isLoading={chatLoading}
            sendMessage={sendMessage}
          />
        </div>
      </div>
    </div>
  );
}
